import styled from "styled-components";
import React, { ChangeEventHandler, CSSProperties } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { Typography } from "../typography";
import * as CONSTANTS from "../../shared/constants";
import { Theme } from "../../shared/types";
import { Icon } from "../icons";

type InputProps = JSX.IntrinsicElements["input"] & {
  error: boolean;
  sx?: CSSProperties;
  width?: string;
  maxlength?: number;
  $hasIcon?: boolean;
};

const Input = styled.input.attrs<InputProps>(({ maxlength }) => ({
  maxLength: maxlength,
}))<InputProps>(({ theme, error, sx, width, $hasIcon }) => ({
  fontFamily: theme.FONT.FAMILY.BODY,
  border: `1px solid ${error ? theme.COLORS.ERROR : theme.COLORS.GRAY_MEDIUM}`,
  boxSizing: "border-box",
  width: width || "100%",
  fontSize: theme.FONT.SIZE.BODY,
  lineHeight: "22px",
  height: "48px",
  color: theme.COLORS.GRAY_BLACK,
  borderRadius: "4px",
  padding: $hasIcon ? "13px 40px 13px 16px" : "13px 16px",
  outline: "none",
  backgroundColor: "transparent",
  "&:disabled": {
    backgroundColor: theme.COLORS.GRAY_X_LIGHT,
  },
  "&:focus": {
    border: `1px solid ${error ? theme.COLORS.ERROR : theme.COLORS.GRAY}`,
  },
  "::placeholder": {
    fontFamily: theme.FONT.FAMILY.BODY,
    color: theme.COLORS.GRAY,
    opacity: 1,
  },
  "::-ms-input-placeholder": {
    fontFamily: theme.FONT.FAMILY.BODY,
    color: theme.COLORS.GRAY,
  },
  ...sx,
}));

type TextAreaProps = Omit<
  TextFieldMultiLineProps,
  "multiline" | "onChange" | "hideSensitiveData"
> & {
  className?: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  testId?: string;
  maxRows?: number;
};
const TextArea = (props: TextAreaProps) => (
  <TextareaAutosize
    className={props.className}
    onChange={props.onChange}
    value={props.value ?? undefined}
    disabled={props.disabled}
    maxLength={props.maxlength}
    maxRows={props.maxRows}
    placeholder={props.placeholder}
    {...(props.testId && { "data-testid": props.testId })}
  />
);

const TextAreaStyled = styled(TextArea)<{ $error: boolean }>`
  resize: none;
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  box-sizing: border-box;
  border: 1px solid
    ${({ $error, theme }) =>
      $error ? theme.COLORS.ERROR : theme.COLORS.GRAY_MEDIUM};
  width: 100%;
  font-size: ${({ theme }) => theme.FONT.SIZE.BODY};
  line-height: 22px;
  min-height: 1rem;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
  border-radius: 4px;
  padding: 14px 16px;
  outline: none;
  background-color: transparent;
  &:disabled {
    background-color: ${({ theme }) => theme.COLORS.GRAY_X_LIGHT};
  }
  &:focus {
    border: 1px solid
      ${({ $error, theme }) =>
        $error ? theme.COLORS.ERROR : theme.COLORS.GRAY};
  }
  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: ${({ theme }) => theme.COLORS.GRAY};
    opacity: 1;
  }
  ::placeholder {
    ::-ms-input-placeholder {
      /* Microsoft Edge */
      color: ${({ theme }) => theme.COLORS.GRAY};
    }
  }
`;

const HintListItemSC = styled.li`
  margin-top: 6px;
  display: flex;
  align-items: center;
  &::before {
    content: "";
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${({ theme }) => theme.COLORS.GRAY_DARK};
    margin-right: 8px;
  }
`;

export const ErrorMessageSC = styled.span<JSX.IntrinsicElements["span"]>`
  color: ${({ theme }) => theme.COLORS.ERROR};
  margin-top: 4px;
  font-size: ${({ theme }) => theme.FONT.SIZE.BODY};
`;

type BaseProps = {
  label?: string;
  placeholder?: string;
  value: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  hints?: string[];
  sensitiveField?: boolean;
  hideSensitiveData: boolean;
  sx?: CSSProperties;
  testId?: string;
  maxlength?: number;
  width?: "default" | "full";
};

type TextFieldSingleLineProps = BaseProps & {
  type?: "password" | "number" | "default";
  onChange: (x: React.ChangeEvent<HTMLInputElement>) => void;
  multiline?: false;
  testId?: string;
  id?: string;
};

type TextFieldMultiLineProps = BaseProps & {
  onChange: (x: React.ChangeEvent<HTMLTextAreaElement>) => void;
  multiline: true;
  unlimitedRows?: boolean;
  testId?: string;
  id?: string;
};

type TextFieldPropsWithIcon = {
  icon: React.ReactNode;
  onIconClick: () => void;
};

type TextFieldPropsWithoutIcon = {
  icon?: never;
  onIconClick?: never;
};

type TextFieldBaseProps = TextFieldSingleLineProps | TextFieldMultiLineProps;
type TextFieldProps =
  | (TextFieldBaseProps & TextFieldPropsWithIcon)
  | (TextFieldBaseProps & TextFieldPropsWithoutIcon);

export const TextField = (props: TextFieldProps) => {
  const hideText =
    props.sensitiveField && props.hideSensitiveData && props.value;
  const text = hideText ? CONSTANTS.DEMO : props.value;
  const onTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    (props as TextFieldMultiLineProps)?.onChange?.(event);
  };
  const onInputElementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    (props as TextFieldSingleLineProps)?.onChange?.(event);
  };
  const theme: Theme = CONSTANTS.defaultTheme; // TODO: Use theme provider like this; const theme = useContext(ThemeContext);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: props.width === "full" ? "100%" : "304px",
      }}
    >
      {!!props.label && (
        <Typography
          style={{ marginBottom: "6px", color: theme.COLORS.GRAY_DARK }}
          variant="body-x-small"
        >
          {props.label}
          {props.required ? "*" : ""}
        </Typography>
      )}
      {"multiline" in props && props.multiline === true ? (
        <TextAreaStyled
          onChange={onTextAreaChange}
          placeholder={props.placeholder}
          maxRows={props.unlimitedRows ? undefined : 8}
          disabled={!!props.disabled}
          $error={!!props.error}
          value={text ?? ""}
          maxlength={props.maxlength}
          {...(props.testId && { "data-testid": props.testId })}
          {...(props.id && { id: props.id })}
        />
      ) : (
        <InputWrapper>
          <Input
            onChange={onInputElementChange}
            maxlength={props.maxlength}
            placeholder={props.placeholder}
            disabled={!!props.disabled}
            error={!!props.error}
            type={props.type}
            sx={props.sx}
            value={text || ""}
            $hasIcon={!!props.icon}
            {...(props.testId && { "data-testid": props.testId })}
            {...(props.id && { id: props.id })}
          />
          {props.icon && (
            <IconButton
              type="button"
              onClick={props.onIconClick}
              aria-label="Toggle"
            >
              {props.icon}
            </IconButton>
          )}
        </InputWrapper>
      )}
      {props.error && !!props.errorMessage && (
        <ErrorContainer>
          <ErrorIcon />
          <Typography variant="body-x-small" color="ERROR">
            {props.errorMessage}
          </Typography>
        </ErrorContainer>
      )}
      {!!props.hints && !props.error && (
        <ul
          style={{
            margin: 0,
            paddingLeft: 8,
            color: theme.COLORS.GRAY_DARK,
          }}
        >
          {props.hints.map((hint) => {
            return (
              <HintListItemSC key={hint}>
                <Typography variant="body-x-small" color="GRAY_DARK">
                  {hint}
                </Typography>
              </HintListItemSC>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const IconButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.COLORS.GRAY};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${CONSTANTS.defaultTheme.SPACING.XS};
`;

const ErrorIcon = styled(Icon.Info)`
  transform: rotate(180deg);
  flex-shrink: 0;
  width: 16px;
  color: ${({ theme }) => theme.COLORS.ERROR};
`;
